import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey,
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey,
  getApiV1IndividualpaymentParcelsParcelidSerialsQueryKey,
  usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidInactivate,
  usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsCancelOpen,
  usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsOpenCancelled,
  usePostApiV1IndividualpaymentParcelsParcelidActivate,
  usePostApiV1IndividualpaymentParcelsParcelidCancel,
  usePostApiV1IndividualpaymentParcelsParcelidCancelInvoice,
  usePostApiV1IndividualpaymentParcelsParcelidReintegrate,
  usePostApiV1IndividualpaymentParcelsParcelidReprocessPayment,
  usePostApiV1IndividualpaymentParcelsParcelidResetIntegration,
  usePostApiV1IndividualpaymentParcelsParcelidReversePayment,
  usePostApiV1IndividualpaymentParcelsParcelidTakeAdvantageVanzolini,
} from '@/shared/api/generated';

import type {
  BatchContractAction,
  ConfirmableContractAction,
  ConfirmableParcelOperation,
} from '../types/parcels';

/** Qualquer uma das ações resolvidas por modal de confirmação. */
export type ConfirmableAction = ConfirmableParcelOperation | ConfirmableContractAction;

/**
 * Operações por parcela cujo payload é só o `parcelId`.
 *
 * `reintegrate` e `cancelInvoice` ficam fora porque levam um dado a mais, e cada
 * uma ganha o seu próprio membro na união — assim o `switch` do `run` recebe o
 * payload já estreitado, sem campo opcional que só se aplica a uma delas.
 */
type PlainParcelOperation = Exclude<ConfirmableParcelOperation, 'reintegrate' | 'cancelInvoice'>;

/**
 * O que executar. Discriminado pela própria ação: as uniões de ação são
 * disjuntas, então o `switch` estreita o payload sem campo extra de escopo.
 */
export type ParcelActionRequest =
  | { readonly action: PlainParcelOperation; readonly parcelId: number }
  | {
      readonly action: 'reintegrate';
      readonly parcelId: number;
      /**
       * Boleto a reintegrar. Omitido quando a ação partiu do menu da parcela, e
       * aí o backend decide o alvo — o parâmetro é opcional no endpoint.
       */
      readonly serial?: number | undefined;
    }
  | {
      readonly action: 'cancelInvoice';
      readonly parcelId: number;
      /** Estornar também o pagamento da parcela junto do cancelamento da NF. */
      readonly reversePayment: boolean;
    }
  | { readonly action: BatchContractAction; readonly parcelIds: readonly number[] }
  | { readonly action: Exclude<ConfirmableContractAction, BatchContractAction> };

/**
 * Executa as treze ações de escrita da tela de parcelas e centraliza a
 * invalidação de cache.
 *
 * As treze mutations geradas pelo Kubb são instanciadas de uma vez — hook não
 * pode ser chamado condicionalmente — e o `run` despacha para a certa. O estado
 * exposto (`isPending`, `error`) é o da ação corrente, então o modal não vê
 * resíduo de uma ação anterior.
 *
 * O componente não invalida nada: cada mutation já invalida no `onSuccess`, por
 * `queryKey` gerada — parcelas e cabeçalho sempre, boletos só nas ações por
 * parcela. O callback `onSuccess` do `run` é para efeito de UI (fechar o modal,
 * alertar), e roda depois da invalidação.
 *
 * @param financialPersonId - Contrato financeiro da rota; `null` torna o `run`
 *   inerte, evitando request com `NaN` na URL.
 * @returns `run`, o estado da ação corrente e `reset` para limpar o erro.
 *
 * @example
 * ```tsx
 * const { run, isPending, error, reset } = useParcelActionRunner(financialPersonId);
 * run({ action: 'cancel', parcelId: 42 }, { onSuccess: () => setModal(null) });
 * ```
 */
export function useParcelActionRunner(financialPersonId: number | null) {
  const queryClient = useQueryClient();
  const [activeAction, setActiveAction] = useState<ConfirmableAction | null>(null);

  const invalidateContract = (parcelId?: number) => {
    if (financialPersonId === null) return;

    void queryClient.invalidateQueries({
      queryKey:
        getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey(financialPersonId),
    });
    void queryClient.invalidateQueries({
      queryKey:
        getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey(financialPersonId),
    });

    // Os boletos são carregados por parcela, sob demanda. Só a parcela tocada
    // muda de estado, então invalidar o contrato inteiro seria desperdício.
    if (parcelId !== undefined) {
      void queryClient.invalidateQueries({
        queryKey: getApiV1IndividualpaymentParcelsParcelidSerialsQueryKey(parcelId),
      });
    }
  };

  const cancelParcel = usePostApiV1IndividualpaymentParcelsParcelidCancel({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const activateParcel = usePostApiV1IndividualpaymentParcelsParcelidActivate({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const reverseParcelPayment = usePostApiV1IndividualpaymentParcelsParcelidReversePayment({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const resetParcelIntegration = usePostApiV1IndividualpaymentParcelsParcelidResetIntegration({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const reintegrateParcel = usePostApiV1IndividualpaymentParcelsParcelidReintegrate({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const cancelInvoice = usePostApiV1IndividualpaymentParcelsParcelidCancelInvoice({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const sendCharge = usePostApiV1IndividualpaymentParcelsParcelidReprocessPayment({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const takeAdvantageParcel = usePostApiV1IndividualpaymentParcelsParcelidTakeAdvantageVanzolini({
    mutation: { onSuccess: (_data, variables) => invalidateContract(variables.parcelId) },
  });
  const cancelOpenParcels =
    usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsCancelOpen({
      mutation: { onSuccess: () => invalidateContract() },
    });
  const openCancelledParcels =
    usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsOpenCancelled({
      mutation: { onSuccess: () => invalidateContract() },
    });
  const inactivateFinancial =
    usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidInactivate({
      mutation: { onSuccess: () => invalidateContract() },
    });

  const mutationByAction = {
    cancel: cancelParcel,
    activate: activateParcel,
    reversePayment: reverseParcelPayment,
    resetIntegration: resetParcelIntegration,
    reintegrate: reintegrateParcel,
    cancelInvoice,
    sendCharge,
    takeAdvantageParcel,
    cancelOpenParcels,
    openCancelledParcels,
    inactivateFinancial,
  } as const satisfies Record<ConfirmableAction, { isPending: boolean; reset: () => void }>;

  const active = activeAction === null ? null : mutationByAction[activeAction];

  const run = (request: ParcelActionRequest, options?: { readonly onSuccess?: () => void }) => {
    if (financialPersonId === null) return;

    setActiveAction(request.action);
    const uiCallbacks = { onSuccess: () => options?.onSuccess?.() };

    switch (request.action) {
      case 'cancel':
        cancelParcel.mutate({ parcelId: request.parcelId }, uiCallbacks);
        return;
      case 'activate':
        activateParcel.mutate({ parcelId: request.parcelId }, uiCallbacks);
        return;
      case 'reversePayment':
        reverseParcelPayment.mutate({ parcelId: request.parcelId }, uiCallbacks);
        return;
      case 'resetIntegration':
        resetParcelIntegration.mutate({ parcelId: request.parcelId }, uiCallbacks);
        return;
      case 'reintegrate':
        // `params` só viaja quando há boleto escolhido: mandar `serial:
        // undefined` produziria `?serial=` na URL, que o backend leria como
        // valor vazio em vez de parâmetro ausente.
        reintegrateParcel.mutate(
          {
            parcelId: request.parcelId,
            ...(request.serial === undefined ? {} : { params: { serial: request.serial } }),
          },
          uiCallbacks,
        );
        return;
      case 'sendCharge':
        sendCharge.mutate({ parcelId: request.parcelId }, uiCallbacks);
        return;
      case 'takeAdvantageParcel':
        // `serialPaymentId` é opcional: sem ele o backend escolhe o boleto
        // excedente. O legado deixava o operador escolher num modal próprio.
        takeAdvantageParcel.mutate({ parcelId: request.parcelId }, uiCallbacks);
        return;
      case 'cancelInvoice':
        cancelInvoice.mutate(
          { parcelId: request.parcelId, params: { reversePayment: request.reversePayment } },
          uiCallbacks,
        );
        return;
      case 'cancelOpenParcels':
        cancelOpenParcels.mutate({ financialPersonId, data: [...request.parcelIds] }, uiCallbacks);
        return;
      case 'openCancelledParcels':
        openCancelledParcels.mutate(
          { financialPersonId, data: [...request.parcelIds] },
          uiCallbacks,
        );
        return;
      case 'inactivateFinancial':
        // `reactivate` é explícito para deixar claro que este caminho só
        // inativa: a reativação existe no mesmo endpoint, mas a tela não sabe
        // se o plano está inativo (o header traz `status` como número sem
        // dicionário), então não é exposta aqui.
        inactivateFinancial.mutate(
          { financialPersonId, params: { reactivate: false } },
          uiCallbacks,
        );
        return;
    }
  };

  const reset = () => {
    active?.reset();
    setActiveAction(null);
  };

  return {
    run,
    reset,
    /** Ação que está em voo ou foi a última executada. */
    activeAction,
    isPending: active?.isPending ?? false,
    error: (active?.error ?? null) as unknown,
  } as const;
}
